// ============================================================================
// Beacon — Learn (LMS). Hosts SCORM courses (Articulate Rise/Storyline exports)
// right inside Beacon. Courses are served same-origin from /public/courses/* so
// the SCORM 1.2 driver can find the LMS API on the parent window; the shim below
// captures completion / score / bookmark and persists per-learner progress.
// New courses are added to the registry; the community requests what it wants.
// ============================================================================

export type Level = "Beginner" | "Intermediate" | "Advanced";

export interface Course {
  id: string;
  title: string;
  description: string;
  topic: string;
  level: Level;
  minutes: number;
  tags: string[];
  emoji: string;
  launch?: string; // same-origin URL to the SCORM launch file; absent = coming soon
}

/** The course library. Live courses have a `launch`; the rest are on the roadmap
 *  (and the community can vote them up in Learn → Requests). */
export const COURSES: Course[] = [
  {
    id: "cap-rate-fundamentals",
    title: "Cap Rate Fundamentals",
    description: "A quick, practical guide to cap rates for real-estate investors — what they are, how to compute them on cost and on value, and how to read them when you're sizing up a deal.",
    topic: "Underwriting", level: "Beginner", minutes: 15,
    tags: ["cap rate", "valuation", "fundamentals"], emoji: "📈",
    launch: "courses/cap-rate-fundamentals/scormdriver/indexAPI.html",
  },
  {
    id: "dscr-mastery",
    title: "DSCR Mastery — Fast-Track Deal Analysis",
    description: "Master the Debt-Service-Coverage Ratio — the number lenders live by. Compute it, stress-test it, and use it to fast-track whether a deal actually finances.",
    topic: "Underwriting", level: "Intermediate", minutes: 20,
    tags: ["DSCR", "financing", "lenders", "deal analysis"], emoji: "🏦",
    launch: "courses/dscr-mastery/scormdriver/indexAPI.html",
  },
  { id: "section8-basics", title: "Section 8 & the Voucher Program", description: "How Housing Choice Vouchers work, why HUD-backed rent is so stable, and what it means to be an approved owner.", topic: "Section 8", level: "Beginner", minutes: 20, tags: ["section 8", "HUD", "vouchers"], emoji: "🏛️" },
  { id: "the-four-gates", title: "The Four Compliance Gates", description: "Physical (NSPIRE), rent reasonableness, program/PHA, and legal/title — the four hard checks every affordable deal must clear.", topic: "Compliance", level: "Intermediate", minutes: 25, tags: ["compliance", "NSPIRE", "gates"], emoji: "🛡️" },
  { id: "due-diligence-leverage", title: "Turning Inspection into Leverage", description: "Run a foolproof due-diligence inspection and convert every documented defect into a justified, profitable revised offer.", topic: "Negotiation", level: "Intermediate", minutes: 30, tags: ["due diligence", "negotiation", "offers"], emoji: "⚖️" },
  { id: "syndication-101", title: "Syndication 101 — Pooling Capital Legally", description: "Reg D 506(b), the working-partners model, sponsor economics, and how to raise inside your own circle the right way.", topic: "Capital & Syndication", level: "Advanced", minutes: 35, tags: ["syndication", "506(b)", "capital"], emoji: "🤝" },
  { id: "fl-insurance", title: "Florida Insurance, De-risked", description: "Why insurance can wreck a Florida deal, how to stress-test a premium spike, and what underwriters actually look for.", topic: "Insurance", level: "Intermediate", minutes: 18, tags: ["insurance", "florida", "risk"], emoji: "🌀" },
];

export const TOPICS = Array.from(new Set(COURSES.map((c) => c.topic)));
export const LEVELS: Level[] = ["Beginner", "Intermediate", "Advanced"];

export function filterCourses(courses: Course[], f: { topic?: string; level?: string; q?: string }): Course[] {
  const q = (f.q || "").trim().toLowerCase();
  return courses.filter((c) =>
    (!f.topic || c.topic === f.topic) &&
    (!f.level || c.level === f.level) &&
    (!q || (c.title + " " + c.description + " " + c.tags.join(" ") + " " + c.topic).toLowerCase().includes(q))
  );
}

// A tiny storage abstraction: localStorage in the browser, in-memory fallback
// when it's unavailable (tests / non-browser). Keeps the shim testable + robust.
const mem: Record<string, string> = {};
const store = {
  get(k: string): string | null { try { return localStorage.getItem(k); } catch { return k in mem ? mem[k] : null; } },
  set(k: string, v: string) { try { localStorage.setItem(k, v); } catch { mem[k] = v; } },
};

// --- learner progress (per device) ------------------------------------------
export type CourseStatus = "not-started" | "in-progress" | "completed";
export interface Progress { status: CourseStatus; score?: number; updatedAt: string; }

const PKEY = "beacon_course_progress";
export function allProgress(): Record<string, Progress> {
  try { return JSON.parse(store.get(PKEY) || "{}"); } catch { return {}; }
}
export function getProgress(courseId: string): Progress | undefined { return allProgress()[courseId]; }
export function setProgress(courseId: string, p: Progress) {
  const all = allProgress(); all[courseId] = p;
  store.set(PKEY, JSON.stringify(all));
}
export function completedCount(): number { return Object.values(allProgress()).filter((p) => p.status === "completed").length; }

// --- SCORM 1.2 runtime shim -------------------------------------------------
// The course (in an iframe) walks window.parent looking for `API`. We put this
// on the app window; it stores the CMI data model and reports progress back.
export interface Scorm12API {
  LMSInitialize: (s: string) => string; LMSFinish: (s: string) => string;
  LMSGetValue: (k: string) => string; LMSSetValue: (k: string, v: string) => string;
  LMSCommit: (s: string) => string; LMSGetLastError: () => string;
  LMSGetErrorString: (c: string) => string; LMSGetDiagnostic: (c: string) => string;
}

const CKEY = (id: string) => "beacon_scorm_" + id;

function statusFrom(cmi: Record<string, string>): CourseStatus {
  const s = (cmi["cmi.core.lesson_status"] || "").toLowerCase();
  if (s === "completed" || s === "passed") return "completed";
  if (s === "failed" || s === "incomplete" || s === "browsed") return "in-progress";
  return "in-progress";
}

/** Build a SCORM 1.2 API for a course, persisting CMI + deriving Progress. */
export function createScorm12API(courseId: string, onProgress?: (p: Progress) => void): Scorm12API {
  let cmi: Record<string, string> = {};
  try { cmi = JSON.parse(store.get(CKEY(courseId)) || "{}"); } catch { /* */ }

  const persist = () => {
    store.set(CKEY(courseId), JSON.stringify(cmi));
    const raw = cmi["cmi.core.score.raw"];
    const p: Progress = { status: statusFrom(cmi), score: raw ? Number(raw) : undefined, updatedAt: new Date().toISOString() };
    setProgress(courseId, p);
    onProgress?.(p);
  };

  const defaults: Record<string, string> = {
    "cmi.core.lesson_status": "not attempted",
    "cmi.core.student_name": "Beacon Learner",
    "cmi.core.student_id": "beacon",
    "cmi.core.credit": "credit",
    "cmi.core.lesson_mode": "normal",
    "cmi.core.entry": cmi["cmi.suspend_data"] || cmi["cmi.core.lesson_location"] ? "resume" : "ab-initio",
    "cmi.suspend_data": "",
    "cmi.core.lesson_location": "",
    "cmi.launch_data": "",
    "cmi.student_data.mastery_score": "",
  };

  return {
    LMSInitialize: () => "true",
    LMSFinish: () => { persist(); return "true"; },
    LMSGetValue: (k) => (k in cmi ? cmi[k] : (defaults[k] ?? "")),
    LMSSetValue: (k, v) => {
      cmi[k] = v;
      if (/lesson_status|score\.raw|suspend_data|lesson_location/.test(k)) persist();
      return "true";
    },
    LMSCommit: () => { persist(); return "true"; },
    LMSGetLastError: () => "0",
    LMSGetErrorString: () => "No error",
    LMSGetDiagnostic: () => "",
  };
}
